"use client"

// Per-tool renderers for the analyst's three tools. Registered by name in the
// tool-renderer registry (see ChatInterface); any other tool falls back to the
// generic ToolCallDisplay. Names and content are tailored to be legible and
// non-technical for the supply analyst — not raw JSON.

import { Package, BarChart3, Braces, AlertTriangle } from "lucide-react"
import type { ToolRenderProps } from "@/hooks/useToolRenderer"
import { ToolDisclosure } from "@/components/ai-elements/tool"
import { CodeBlock } from "@/components/ai-elements/code-block"
import type { ReactNode } from "react"

/** Parse a (possibly partial, mid-stream) tool-input JSON string; null if not yet valid. */
function parseArgs(args: string): Record<string, unknown> | null {
  if (!args) return null
  try {
    return JSON.parse(args) as Record<string, unknown>
  } catch {
    // Input streams in as partial JSON; it just isn't parseable yet.
    return null
  }
}

/** Parse a tool result that is expected to be a JSON object (descriptor/envelope). */
function parseJsonResult(result?: string): Record<string, unknown> | null {
  if (!result) return null
  try {
    return JSON.parse(result) as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * Extract the exact `description` from a SKILL.md frontmatter block. The
 * read_skill result is the full SKILL.md (frontmatter + body), so the header
 * description is parseable client-side. Handles inline and folded (`>` / `|`)
 * YAML scalars without a YAML dependency.
 */
function extractSkillDescription(body: string): string | null {
  const frontmatter = body.match(/^---\s*\n([\s\S]*?)\n---/)
  if (!frontmatter) return null
  const yaml = frontmatter[1]
  const lines = yaml.split("\n")
  const idx = lines.findIndex(l => /^description\s*:/.test(l))
  if (idx === -1) return null

  const inline = lines[idx].replace(/^description\s*:/, "").trim()
  // Inline scalar (not a block indicator) — strip surrounding quotes.
  if (inline && !/^[>|][+-]?$/.test(inline)) {
    return inline.replace(/^["']|["']$/g, "").trim() || null
  }
  // Folded/literal block: collect the subsequent more-indented lines.
  const collected: string[] = []
  for (let i = idx + 1; i < lines.length; i += 1) {
    const line = lines[i]
    if (line.trim() === "") {
      collected.push("")
    } else if (/^\s+\S/.test(line)) {
      collected.push(line.trim())
    } else {
      break
    }
  }
  const text = collected.join(" ").replace(/\s+/g, " ").trim()
  return text || null
}

/** Format a param value for display: scalars as-is, objects/arrays as compact JSON. */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—"
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

/** Shared error block, shown when a tool result is an error descriptor. */
function ToolError({ message, hint }: { message: string; hint?: string }): ReactNode {
  return (
    <div className="flex items-start gap-1.5 rounded border border-warn/30 bg-warn/10 px-2 py-1.5 text-xs text-warn">
      <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        <div className="break-words">{message}</div>
        {hint ? <div className="mt-0.5 break-words opacity-90">Sugerencia: {hint}</div> : null}
      </div>
    </div>
  )
}

/** read_skill → "Cargando módulo": shows the module's exact header description. */
export function LoadModuleTool({ args, status, result }: ToolRenderProps): ReactNode {
  const input = parseArgs(args)
  const moduleName = typeof input?.module === "string" ? input.module : undefined
  const description = result ? extractSkillDescription(result) : null

  return (
    <ToolDisclosure
      icon={<Package className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
      title="Cargando módulo"
      summary={moduleName}
      status={status}
      statusLabels={{ streaming: "cargando…", executing: "cargando…", complete: "listo" }}
    >
      {description ? (
        <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
      ) : result ? (
        <p className="text-xs text-muted-foreground">Sin descripción disponible.</p>
      ) : null}
    </ToolDisclosure>
  )
}

/** run_script → "Ejecutando análisis": shows params and a plumbing-free result summary. */
export function RunScriptTool({ args, status, result }: ToolRenderProps): ReactNode {
  const input = parseArgs(args)
  const script = typeof input?.script === "string" ? input.script : undefined
  const params =
    input?.params && typeof input.params === "object"
      ? (input.params as Record<string, unknown>)
      : undefined
  const desc = parseJsonResult(result)

  const isError = desc != null && (desc.ok === false || typeof desc.error_type === "string")
  const resultType = typeof desc?.type === "string" ? desc.type : undefined
  const resultSummary = typeof desc?.summary === "string" ? desc.summary : undefined

  const paramEntries = params ? Object.entries(params) : []

  return (
    <ToolDisclosure
      icon={<BarChart3 className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
      title="Ejecutando análisis"
      summary={script}
      status={status}
      statusLabels={{ streaming: "preparando…", executing: "ejecutando…", complete: "completado" }}
    >
      <div>
        <div className="mb-1 text-xs text-muted-foreground">Parámetros</div>
        {paramEntries.length > 0 ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
            {paramEntries.map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="font-mono text-muted-foreground">{key}</dt>
                <dd className="break-words font-mono text-foreground">{formatValue(value)}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-xs text-muted-foreground">Sin parámetros.</p>
        )}
      </div>

      {isError ? (
        <ToolError
          message={typeof desc?.message === "string" ? desc.message : "Error en el análisis."}
          hint={typeof desc?.recovery_hint === "string" ? desc.recovery_hint : undefined}
        />
      ) : resultType || resultSummary ? (
        <div>
          <div className="mb-1 text-xs text-muted-foreground">Resultado</div>
          <div className="text-xs text-foreground">
            {[resultType, resultSummary].filter(Boolean).join(" · ")}
          </div>
        </div>
      ) : null}
    </ToolDisclosure>
  )
}

/** run_code → "Ejecutando código": shows the Python, syntax-highlighted, plus any output. */
export function RunCodeTool({ args, status, result }: ToolRenderProps): ReactNode {
  const input = parseArgs(args)
  const code = typeof input?.code === "string" ? input.code : ""
  const env = parseJsonResult(result)

  const output =
    typeof env?.output === "string"
      ? env.output
      : typeof env?.stdout === "string"
        ? env.stdout
        : null
  // run_code's error is a descriptor object ({message, recovery_hint, …}); also
  // tolerate a plain string just in case.
  const errorObj =
    env?.error && typeof env.error === "object" ? (env.error as Record<string, unknown>) : null
  const errorMessage = errorObj
    ? typeof errorObj.message === "string"
      ? errorObj.message
      : "Error al procesar."
    : typeof env?.error === "string"
      ? env.error
      : null
  const errorHint =
    errorObj && typeof errorObj.recovery_hint === "string" ? errorObj.recovery_hint : undefined

  return (
    <ToolDisclosure
      icon={<Braces className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
      title="Ejecutando código"
      status={status}
      statusLabels={{ streaming: "escribiendo…", executing: "ejecutando…", complete: "completado" }}
    >
      {code ? <CodeBlock code={code} language="python" /> : null}
      {output ? (
        <div>
          <div className="mb-1 text-xs text-muted-foreground">Salida</div>
          <pre className="whitespace-pre-wrap break-words font-mono text-xs text-foreground/80">
            {output}
          </pre>
        </div>
      ) : null}
      {errorMessage ? <ToolError message={errorMessage} hint={errorHint} /> : null}
    </ToolDisclosure>
  )
}
